import React, { useEffect, useRef, useState } from "react";

import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Checkbox, Text } from "@performyard/py-components";

import ContentModal from "../../../../components/Modal/Templates/ContentModal";

import { REMOVE_CALENDAR_CONFIG, STATUS_CONFIRMATION_CONFIG } from "../config";

type StatusConfirmationModalProps = {
	isOpen: boolean;
	isLoading: boolean;
	status: MeetingStatus | null;
	action?: "status-change" | "remove-from-calendar";
	onClose: () => void;
	onConfirm: (removeFromCalendar?: boolean) => void;
	hasConnectedCalendarEvent?: boolean;
};

/**
 * Confirmation modal for status changes and calendar removal.
 * Shows appropriate message based on the action type.
 */
const StatusConfirmationModal = ({
	isOpen,
	isLoading,
	status,
	action = "status-change",
	onClose,
	onConfirm,
	hasConnectedCalendarEvent = false,
}: StatusConfirmationModalProps): React.ReactElement => {
	const [removeFromCalendar, setRemoveFromCalendar] = useState(false);

	const contentRef = useRef<{
		config: { title: string; message: string; confirmText: string; confirmButtonColor: "green" | "red" } | null;
		showCheckbox: boolean;
	}>({ config: null, showCheckbox: false });

	const getActionConfig = (
		actionType: "status-change" | "remove-from-calendar",
		statusValue: MeetingStatus | null
	): { title: string; message: string; confirmText: string; confirmButtonColor: "green" | "red" } | null => {
		if (actionType === "remove-from-calendar") {
			return REMOVE_CALENDAR_CONFIG;
		}

		if (statusValue && statusValue in STATUS_CONFIRMATION_CONFIG) {
			return STATUS_CONFIRMATION_CONFIG[statusValue as keyof typeof STATUS_CONFIRMATION_CONFIG];
		}

		return null;
	};

	useEffect(() => {
		if (isOpen) {
			const modalConfig = getActionConfig(action, status);
			const showCheckbox = action === "status-change" && status === "canceled" && hasConnectedCalendarEvent;
			contentRef.current = { config: modalConfig, showCheckbox };
		}
	}, [isOpen, action, status, hasConnectedCalendarEvent]);

	const modalConfig = isOpen ? getActionConfig(action, status) : contentRef.current.config;
	const showCalendarCheckbox = isOpen
		? action === "status-change" && status === "canceled" && hasConnectedCalendarEvent
		: contentRef.current.showCheckbox;

	const handleConfirm = (): void => {
		onConfirm(removeFromCalendar);
	};

	const handleClose = (): void => {
		setRemoveFromCalendar(false);
		onClose();
	};

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={isLoading ? () => {} : handleClose}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{modalConfig?.title}
				</Text>
			}
			buttons={[
				<ButtonStandard
					key="cancel"
					variant="solid"
					color="primary"
					size="large"
					onClick={handleClose}
					disabled={isLoading}
					dangerouslySetClassName="w-fit"
				>
					Cancel
				</ButtonStandard>,
				<ButtonStandard
					key="confirm"
					variant="solid"
					color={modalConfig?.confirmButtonColor}
					size="large"
					onClick={handleConfirm}
					disabled={isLoading}
					dangerouslySetClassName="w-fit"
				>
					{modalConfig?.confirmText}
				</ButtonStandard>,
			]}
		>
			<div className="space-y-4 text-center">
				<Text size="paragraph-1" weight="regular">
					{modalConfig?.message}
				</Text>
				{showCalendarCheckbox && (
					<div className="flex items-center justify-center space-x-2">
						<Checkbox
							id="remove-from-calendar"
							checked={removeFromCalendar}
							onCheckedChange={setRemoveFromCalendar}
							disabled={isLoading}
							aria-label="Remove from My Calendar"
						/>
						<Text size="paragraph-3" weight="regular">
							Remove from My Calendar
						</Text>
					</div>
				)}
			</div>
		</ContentModal>
	);
};

export default StatusConfirmationModal;
